import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import type { RelationFile } from '@risksmart-app/shared/forms/shared-schemas/fileSchema';
import type { FC } from 'react';
import { useNavigate } from 'react-router';
import { ownerAndContributorIds } from 'src/components/form';
import { useI18NSummaryHelpContent } from 'src/components/help-panel/useSummaryHelpContent';

import { useInsertAsset } from '@/hooks/mutations/asset';
import { useFileUpdate } from '@/hooks/mutations/file/useFileUpdate';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useIsFormPrefillEnabled } from '@/hooks/useIsFormPrefillEnabled';
import { createWithFiles } from '@/utils/createWithFiles';
import { assetDetailsUrl } from '@/utils/urls';

import AssetForm from '../../update/forms/AssetForm';
import type { AssetFormFieldData } from '../../update/forms/assetSchema';

const AssetCreateTab: FC = () => {
  useI18NSummaryHelpContent('assets.help');
  const navigate = useNavigate();
  const { updateFiles } = useFileUpdate();
  const { insertAsset } = useInsertAsset();
  const objectToolbarEnabled = useIsFeatureFlagEnabled('dynamic_forms');
  const formPrefillEnabled = useIsFormPrefillEnabled();

  const onSave = async (values: AssetFormFieldData) => {
    const { createdEntity } = await createWithFiles({
      create: () =>
        insertAsset({
          Title: values.Title,
          Status: values.Status,
          Description: values.Description ?? null,
          AssetType: values.AssetType ?? null,
          SerialNumber: values.SerialNumber ?? null,
          PurchaseDate: values.PurchaseDate ?? null,
          Location: values.Location ?? null,
          Cost: values.Cost ?? null,
          DepreciationMethod: values.DepreciationMethod ?? null,
          Assignee: values.Assignee?.value ?? null,
          CustomAttributeData: values.CustomAttributeData ?? undefined,
          DepartmentTypeIds:
            values.departments?.map((d) => d.DepartmentTypeId) || [],
          TagTypeIds: values.tags?.map((t) => t.TagTypeId) || [],
          ...ownerAndContributorIds(values),
        }),
      getCreatedEntity: (result) => {
        const assetId = result.insertChildAsset?.Id;
        if (!assetId) {
          throw new Error('Missing assetId');
        }

        return { id: assetId, destination: assetDetailsUrl(assetId) };
      },
      saveFiles: ({ id }) =>
        updateFiles({
          parentType: Parent_Type_Enum.Asset,
          parentId: id,
          originalFiles: values.files?.filter(
            (f) => !(f instanceof File)
          ) as RelationFile[],
          selectedFiles: values.files,
        }),
    });

    return {
      savedIds: [createdEntity.id],
      createdEntity,
      successNavigation: {
        destination: createdEntity.destination,
        isReplace: true,
      },
    };
  };

  const onDismiss = (saved?: boolean) => {
    if (!saved) {
      navigate(-1);
    }
  };

  return (
    <AssetForm
      onSave={onSave}
      onDismiss={onDismiss}
      publishToolbarState={objectToolbarEnabled}
      publishActiveForm={formPrefillEnabled}
    />
  );
};

export default AssetCreateTab;
